'use client'

import { motion } from 'motion/react'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { useMeasure } from 'react-use'
import { useGetAllTagsQuery } from '@/api/tags/get-all-tags-query'
import { Button } from '@/components/button'
import { Gradient } from '@/components/gradient'
import { Tag } from '@/components/tag'
import {
    IRestaurantDisplayTagsRenderTag,
    RestaurantCitySearchFiltersDisplayTagsDesktop,
} from '@/features/restaurants/restaurants-city-search/restaurant-city-search-filters/tags/desktop/restaurant-city-search-filters-display-tags-desktop'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { COLORS } from '@/utils/styles/colors'

const TAGS_Y_SPACING = 12

export const RestaurantCitySearchFiltersSelectTagsDesktop = () => {
    const tagsRefs = useRef<Record<string, HTMLDivElement>>({})

    const { city } = useRestaurantCitySearchContext()

    const { data } = useGetAllTagsQuery(city?.slug)

    const tags = data?.data

    const [tagsContainerRef, { width: tagsContainerWidth }] = useMeasure<HTMLDivElement>()
    const tagsContainerScrollRef = useRef<HTMLDivElement>(null)

    const [tagsPage, setTagsPage] = useState(1)
    const [tagsPagesCount, setTagsPagesCount] = useState(1)

    useLayoutEffect(() => {
        if (!tagsContainerScrollRef.current) return

        tagsContainerScrollRef.current.scrollTo({
            left: tagsContainerWidth * (tagsPage - 1),
            behavior: 'smooth',
        })
    }, [tagsPage, tagsContainerWidth])

    const calculateTagsPagesCount = useCallback(() => {
        if (!tagsContainerWidth || !tags) return

        const numberOfTagRefs = Object.keys(tagsRefs.current).length
        if (numberOfTagRefs !== tags.length) return

        const tagsWidth = Object.values(tagsRefs.current).reduce((acc, tag, index, arr) => {
            const isLast = index === arr.length - 1

            return acc + tag?.offsetWidth + (isLast ? 0 : TAGS_Y_SPACING)
        }, 0)

        const tagsPagesCount = Math.ceil(tagsWidth / tagsContainerWidth)

        if (Number.isNaN(tagsPagesCount)) return

        setTagsPagesCount(tagsPagesCount)
    }, [tagsContainerWidth, tags])

    const makeTagRef = useCallback(
        (label: string, isLast: boolean) => (element: HTMLDivElement) => {
            tagsRefs.current[label] = element

            if (isLast) {
                calculateTagsPagesCount()
            }
        },
        [calculateTagsPagesCount]
    )

    useLayoutEffect(() => {
        calculateTagsPagesCount()
    }, [calculateTagsPagesCount])

    const renderTag = useCallback<IRestaurantDisplayTagsRenderTag>(
        (tag, { toggleTag, isLast }) => (
            <Tag tag={tag} active={false} onClick={toggleTag} ref={makeTagRef(tag.slug, isLast)} />
        ),
        [makeTagRef]
    )

    return (
        <div className="flex flex-row w-full gap-x-4 justify-between scrollbar-hide">
            <Button
                shape="circle"
                styleType="tertiary"
                disabled={tagsPage === 1}
                onClick={() => {
                    setTagsPage((prev) => Math.max(1, prev - 1))
                }}
                className="hidden md:flex"
            >
                <SvgOutlinedChevronRight
                    color={COLORS.black}
                    size={24}
                    style={{ rotate: '180deg' }}
                />
            </Button>

            <div className="min-w-2 block md:hidden" />

            <div
                ref={tagsContainerRef}
                className="flex flex-row items-center md:w-full md:max-w-full md:overflow-hidden scrollbar-hide relative"
            >
                <motion.div
                    initial={{ opacity: tagsPage === 1 ? 0 : 1 }}
                    animate={{ opacity: tagsPage === 1 ? 0 : 1 }}
                    transition={{ duration: 1 }}
                >
                    <Gradient className="absolute left-0 top-0 min-w-[20px]" direction="to-right" />
                </motion.div>

                <div
                    ref={tagsContainerScrollRef}
                    className="w-full flex flex-row items-center overflow-hidden justify-start"
                    style={{
                        columnGap: `${TAGS_Y_SPACING}px`,
                    }}
                >
                    <RestaurantCitySearchFiltersDisplayTagsDesktop
                        type="inactive"
                        renderTag={renderTag}
                    />
                </div>

                <motion.div
                    initial={{ opacity: tagsPage === tagsPagesCount ? 0 : 1 }}
                    animate={{ opacity: tagsPage === tagsPagesCount ? 0 : 1 }}
                    transition={{ duration: 1 }}
                >
                    <Gradient className="absolute right-0 top-0 min-w-[20px]" />
                </motion.div>
            </div>

            <div className="min-w-2 block md:hidden" />

            <Button
                shape="circle"
                styleType="tertiary"
                disabled={tagsPage === tagsPagesCount}
                onClick={() => {
                    setTagsPage((prev) => Math.min(tagsPagesCount, prev + 1))
                }}
                className="hidden md:flex"
            >
                <SvgOutlinedChevronRight color={COLORS.black} size={24} />
            </Button>
        </div>
    )
}
